import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  ColumnDef,
  ColumnFiltersState,
  SortingState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ChevronDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface DataTableProps<TData> {
  data: TData[];
  columns: ColumnDef<TData>[];
  hiddenColumnsOnMobile?: string[];
  disableSearch?: boolean;
  disableColumnSelector?: boolean;
  emptyText?: string;
}

export function DataTable<TData>({
  data,
  columns,
  hiddenColumnsOnMobile = [],
  disableSearch,
  disableColumnSelector,
  emptyText = 'general.no_data',
}: DataTableProps<TData>) {
  const { t } = useTranslation();
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
  const [rowSelection, setRowSelection] = useState({});
  const [globalFilter, setGlobalFilter] = useState('');

  const stableHiddenColumnsOnMobile = useMemo(
    () => [...hiddenColumnsOnMobile],
    [hiddenColumnsOnMobile],
  );

  const updateColumnVisibility = useCallback(() => {
    const isMobile = window.innerWidth < 768;
    setColumnVisibility((prev) => {
      let hasChanges = false;
      const newState: VisibilityState = { ...prev };

      stableHiddenColumnsOnMobile.forEach((col) => {
        const newValue = isMobile ? false : (prev[col] ?? true);
        if (newState[col] !== newValue) {
          hasChanges = true;
          newState[col] = newValue;
        }
      });

      return hasChanges ? newState : prev;
    });
  }, [stableHiddenColumnsOnMobile]);

  useEffect(() => {
    updateColumnVisibility();
    window.addEventListener('resize', updateColumnVisibility);
    return () => window.removeEventListener('resize', updateColumnVisibility);
  }, [updateColumnVisibility]);

  const table = useReactTable({
    data,
    columns,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    onGlobalFilterChange: setGlobalFilter,
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
      globalFilter,
    },
    globalFilterFn: (row, columnId, filterValue) => {
      return String(row.getValue(columnId)).toLowerCase().includes(filterValue.toLowerCase());
    },
  });

  return (
    <div className="max-w-full px-6">
      <div className="flex items-center">
        {!disableSearch && (
          <Input
            placeholder={t('general.search')}
            value={globalFilter}
            onChange={(event) => setGlobalFilter(event.target.value)}
            className="max-w-[200px] my-4"
          />
        )}

        {!disableColumnSelector && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="ml-auto my-4">
                {t('general.columns')}
                <ChevronDown className="ml-2 h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {table
                .getAllColumns()
                .filter((column) => column.getCanHide())
                .map((column) => {
                  if (typeof column.columnDef.header !== 'string') {
                    return;
                  }

                  return (
                    <DropdownMenuCheckboxItem
                      key={column.id}
                      checked={column.getIsVisible()}
                      onCheckedChange={(value) => column.toggleVisibility(!!value)}
                    >
                      {column.columnDef.header}
                    </DropdownMenuCheckboxItem>
                  );
                })}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead key={header.id}>
                    {flexRender(header.column.columnDef.header, header.getContext())}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow key={row.id}>
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={columns.length} className="h-24 text-center">
                  {t(emptyText)}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {table.getPageCount() > 1 && (
        <div className="flex items-center justify-end space-x-2 py-4">
          {table.getCanPreviousPage() && (
            <Button variant="outline" size="sm" onClick={() => table.previousPage()}>
              {t('general.back')}
            </Button>
          )}
          {table.getCanNextPage() && (
            <Button variant="outline" size="sm" onClick={() => table.nextPage()}>
              {t('general.further')}
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
